import { useMemo } from 'react';
import { getEnabledIdTypes, type IdTypeEntry } from '@helios/data-access';
import { useRegionalSettings } from './useFormatMoney';

/**
 * Reactive list of currently-enabled ID type entries (code + label) from
 * `app_config.extra_json.regional_settings.id_types`. Falls back to PH
 * defaults when the config row or its regional_settings block is missing
 * or malformed (`getRegionalSettings` handles that path).
 *
 * The returned entries are stable: `code` is the persistent internal key
 * managed by Clinic Settings; `label` is what the dropdown displays.
 */
export function useRegionalIdTypes(): IdTypeEntry[] {
  const regional = useRegionalSettings();
  return useMemo(() => getEnabledIdTypes(regional), [regional]);
}

/**
 * Convenience hook for legacy patient-identity dropdowns that store a
 * plain label string in `patient_identifiers.id_type`. Returns the
 * enabled labels in their configured order, optionally prepending a
 * caller-supplied current value when that value is no longer enabled
 * so the form can still render the existing selection without losing
 * data (per PRD.232: existing saved values must not be cleared).
 */
export function useEnabledIdTypeLabels(currentValue?: string | null): string[] {
  const idTypes = useRegionalIdTypes();
  return useMemo(() => {
    const labels = idTypes.map((t) => t.label);
    const cur = currentValue?.trim();
    if (cur && !labels.includes(cur)) {
      return [cur, ...labels];
    }
    return labels;
  }, [idTypes, currentValue]);
}
